import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from 'apollo-boost';
import { GraphQLError } from 'graphql';

import { TravellerFieldEnum, useFindOrderMutation } from '@websky/graphql';
import { Props } from './BookingPreview';
import { capitalizeFirstLetterInName, format, getUserValue, redirectToOrder, removeDotsFromDate } from '../../utils';
import { useConfig } from '../../context';
import { useLocation } from 'react-router-dom';
import { useHistory } from '@shared/routing';
import { getUrlWithServicesParams } from '../../Checkout/utils';
import { sessionStorageSet } from '../../cache';
import { getSaveOrderToAccountShownKey } from '../../AnonymousOrderModal/utils';

const ATTEMPT_TO_IMPORT_ORDER_ERROR = 'Попытка импорта заказа не удалась';

type GraphQLErrorWithDebugMessage = GraphQLError & { debugMessage?: string };

export function useBookingPreview({ order, onClearOrder, onViewOrderClick, redirectViaHistory }: Props) {
	const { t } = useTranslation('BookingPreview');

	const history = useHistory();
	const location = useLocation();

	const {
		Account: { engineURL }
	} = useConfig();

	const [error, setError] = useState(null);
	const [isSaveLoading, setIsSaveLoading] = useState(false);

	const [findOrder, { loading }] = useFindOrderMutation();

	const formatDate = useCallback((date: string) => removeDotsFromDate(format(date, 'dd MMM, yyyy')), []);

	const { departure, arrival } = useMemo(() => {
		const segmentGroups = order?.flight?.segmentGroups;

		const departure = segmentGroups?.[0]?.segments[0]?.departure;
		const arrival = segmentGroups?.[segmentGroups.length - 1]?.segments?.[0].arrival;

		return { departure, arrival };
	}, [order?.flight]);

	const date = useMemo(() => {
		if (!order) {
			return null;
		}

		if (order?.flight?.segmentGroups?.length > 1) {
			if (departure.date === arrival.date) {
				return `${formatDate(departure.date)}, ${departure.time} - ${arrival.time}`;
			} else {
				return `${formatDate(departure.date)} - ${formatDate(arrival.date)}`;
			}
		}

		return formatDate(departure.date);
	}, [order?.flight]);

	const travellersCount = useMemo(() => order?.travellers?.length ?? 0, [order?.travellers]);

	const onError = useCallback(
		(e: unknown) => {
			let errorMessage = 'Unknown error';

			if (e instanceof ApolloError && e.graphQLErrors?.[0]?.message) {
				const gqlError = e.graphQLErrors[0] as GraphQLErrorWithDebugMessage;

				if ((gqlError.debugMessage ?? gqlError.message).includes(ATTEMPT_TO_IMPORT_ORDER_ERROR)) {
					errorMessage = 'The attempt to import the order failed';
				} else {
					errorMessage = gqlError.message;
				}
			} else if (e instanceof Error && e.message) {
				errorMessage = e.message;
			}

			setError(t(errorMessage));
		},
		[setError]
	);

	const onClearError = useCallback(() => {
		setError(null);
	}, []);

	const onViewOrder = useCallback(() => {
		if (redirectViaHistory) {
			const bookingURL = `/booking/${order.id}/order`;
			const isBookingRoute = location.pathname.includes(bookingURL);

			if (isBookingRoute) {
				window.location.reload();
			} else {
				history.push(getUrlWithServicesParams(bookingURL, location));
			}
		} else {
			redirectToOrder(engineURL, order.id, url => getUrlWithServicesParams(url, location));
		}

		/** WEBSKY-6759: Логика прикрепления анонимных заказов  */
		sessionStorageSet(getSaveOrderToAccountShownKey(order.id), true);

		onViewOrderClick?.();
	}, [engineURL, order, redirectViaHistory, history, location, onViewOrderClick]);

	const onSaveAndView = useCallback(async () => {
		setIsSaveLoading(true);

		let accessCode = order.accessCode;

		if (order.travellers?.length) {
			accessCode = capitalizeFirstLetterInName(getUserValue(order.travellers[0], TravellerFieldEnum.LastName));
		}

		try {
			const { data } = await findOrder({
				variables: {
					params: {
						id: order.locator,
						secret: accessCode,
						saveInProfile: true
					}
				}
			});

			if (!data?.FindOrder) {
				return;
			}

			setIsSaveLoading(false);

			onViewOrder();
			onClearOrder?.();
		} catch (e) {
			onError(e);

			setIsSaveLoading(false);
		}
	}, [order, findOrder, setIsSaveLoading, onViewOrder, onClearOrder, onError]);

	return {
		// data
		departure,
		arrival,
		date,
		travellersCount,
		error,
		isLoading: loading,
		isSaveLoading,

		// methods
		onViewOrder,
		onSaveAndView,
		onClearError
	};
}
